/**
 * All Farm View sorting helpers.
 * Shared by the web and mobile farm view / field list sorting.
 */

/**
 * Normalises a timestamp to a comparable number.
 * Accepts numbers, numeric strings (seconds or ms) and ISO strings.
 * @param {string|number|null|undefined} ts
 * @returns {number} the numeric timestamp, or -1 when it cannot be parsed
 */
var parseSortTimestamp = function (ts) {
  if (ts === null || ts === undefined) {
    return -1;
  }
  if (typeof ts === 'number') {
    return Number.isFinite(ts) ? ts : -1;
  }
  if (typeof ts === 'string') {
    var trimmed = ts.trim();
    if (trimmed.length === 0) {
      return -1;
    }
    // Numeric timestamps (seconds or ms) sometimes come as strings
    var numeric = Number(trimmed);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
    // ISO timestamp
    var parsed = Date.parse(trimmed);
    return Number.isFinite(parsed) ? parsed : -1;
  }
  return -1;
};

/**
 * How long the last *completed* irrigation ran for.
 * A field that never irrigated, or one that is still irrigating (no end
 * timestamp yet), has no duration to sort by.
 *
 * Both timestamps must come from the same source: pairing a live start with a
 * historical end would measure two different irrigation events.
 *
 * @param {string|number|null} startedAt - last irrigation start
 * @param {string|number|null} endedAt - last irrigation end
 * @returns {number|null} duration in the timestamps' own units, or null when unknown
 */
var lastIrrigationDurationValue = function (startedAt, endedAt) {
  var start = parseSortTimestamp(startedAt);
  var end = parseSortTimestamp(endedAt);
  if (start < 0 || end < 0) {
    return null;
  }
  var duration = end - start;
  return duration >= 0 ? duration : null;
};

/**
 * Compares two last-irrigation durations.
 * Fields with no known duration always sink to the bottom, in both directions,
 * so flipping the sort never floats "no data" rows to the top.
 *
 * @param {number|null} aDuration
 * @param {number|null} bDuration
 * @param {boolean} ascending - true for shortest → longest
 * @returns {number}
 */
var compareLastIrrigationDuration = function (aDuration, bDuration, ascending) {
  var aMissing = aDuration === null || aDuration === undefined;
  var bMissing = bDuration === null || bDuration === undefined;
  if (aMissing && bMissing) {
    return 0;
  }
  if (aMissing) {
    return 1;
  }
  if (bMissing) {
    return -1;
  }
  return ascending ? aDuration - bDuration : bDuration - aDuration;
};

// Irrigation amounts come in the field's length unit; compare them in mm.
var MM_PER_LENGTH_UNIT = {
  mm: 1,
  cm: 10,
  in: 25.4,
  inch: 25.4,
  inches: 25.4,
};

var amountInMm = function (amount, unit) {
  var value = typeof amount === 'number' ? amount : parseFloat(amount);
  if (!Number.isFinite(value)) {
    return null;
  }
  var factor =
    MM_PER_LENGTH_UNIT[
      String(unit || 'mm')
        .trim()
        .toLowerCase()
    ];
  return value * (factor || 1);
};

/**
 * Sort key for the "Last irrigation (Shortest → Longest / Longest → Shortest)"
 * sorts: the value the row actually shows next to its last irrigation.
 * Duration-display fields show `(2hr 30min)`, every other field shows the last
 * reported amount `(4.2 mm)` — sorting the latter by a hidden duration put
 * `1 mm` before `0 mm` (QA reopen).
 *
 * Mirrors the row's display rule: no detail is shown (and the key is null)
 * unless both timestamps are present. Callers pass null timestamps for any
 * other case where their row hides the detail (e.g. mobile while irrigating).
 *
 * @param {Object} row
 * @param {boolean} row.showDuration - whether the row shows a duration
 * @param {string|number|null} row.startedAt - last irrigation start
 * @param {string|number|null} row.endedAt - last irrigation end
 * @param {number|string|null} row.amount - last irrigation amount (wfr_length)
 * @param {string|null} row.amountUnit - the amount's length unit
 * @returns {{kind: string, value: number}|null} kind is 'duration' (ms) or 'amount' (mm)
 */
var lastIrrigationDisplayedSortKey = function (row) {
  var start = parseSortTimestamp(row && row.startedAt);
  var end = parseSortTimestamp(row && row.endedAt);
  if (start < 0 || end < 0) {
    return null;
  }
  var value = row.showDuration
    ? lastIrrigationDurationValue(row.startedAt, row.endedAt)
    : amountInMm(row.amount, row.amountUnit);
  if (value === null) {
    return null;
  }
  return { kind: row.showDuration ? 'duration' : 'amount', value: value };
};

/**
 * Compares two lastIrrigationDisplayedSortKey results.
 * Durations and amounts can't be compared with each other, so duration rows
 * come first and amount rows second, each ordered by `ascending`. Rows with
 * nothing shown sink to the bottom in both directions.
 *
 * @param {{kind: string, value: number}|null} aKey
 * @param {{kind: string, value: number}|null} bKey
 * @param {boolean} ascending - true for shortest → longest
 * @returns {number}
 */
var compareLastIrrigationDisplayed = function (aKey, bKey, ascending) {
  if (aKey && bKey && aKey.kind !== bKey.kind) {
    return aKey.kind === 'duration' ? -1 : 1;
  }
  return compareLastIrrigationDuration(
    aKey ? aKey.value : null,
    bKey ? bKey.value : null,
    ascending
  );
};

module.exports = {
  parseSortTimestamp: parseSortTimestamp,
  lastIrrigationDurationValue: lastIrrigationDurationValue,
  compareLastIrrigationDuration: compareLastIrrigationDuration,
  lastIrrigationDisplayedSortKey: lastIrrigationDisplayedSortKey,
  compareLastIrrigationDisplayed: compareLastIrrigationDisplayed,
};
